import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import axios from "axios";
import {
  capitalizeFirst,
  getProfessionalTypeLabel,
} from "../../utils/formatters";

function StaffProfile() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");

    // Fetch staff profile
    axios
      .get(`${API_BASE_URL}/api/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setStaff(res.data);
        setLoading(false);
        // Fetch bookings and analytics after getting staff data
        fetchBookingsAndAnalytics(res.data._id, token);
      })
      .catch((err) => {
        setStaff(null);
        setLoading(false);
        setLoadingBookings(false);
        setLoadingAnalytics(false);
      });
  }, [staffId, API_BASE_URL]);

  const fetchBookingsAndAnalytics = async (staffId, token) => {
    try {
      // Fetch bookings for this staff member
      const bookingsRes = await axios.get(
        `${API_BASE_URL}/api/bookings/staff/${staffId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const staffBookings = bookingsRes.data.bookings || [];
      setBookings(staffBookings);

      // Calculate analytics
      const now = new Date();
      const thisMonth = staffBookings.filter((b) => {
        const bookingDate = new Date(b.createdAt);
        return (
          bookingDate.getMonth() === now.getMonth() &&
          bookingDate.getFullYear() === now.getFullYear()
        );
      });

      const completed = staffBookings.filter((b) => b.status === "completed");
      const totalRevenue = completed.reduce(
        (sum, b) => sum + (b.totalAmount || 0),
        0,
      );
      const monthlyRevenue = thisMonth
        .filter((b) => b.status === "completed")
        .reduce((sum, b) => sum + (b.totalAmount || 0), 0);

      setAnalytics({
        totalBookings: staffBookings.length,
        completedBookings: completed.length,
        totalRevenue,
        monthlyRevenue,
        thisMonthBookings: thisMonth.length,
        upcomingBookings: staffBookings.filter((b) => {
          const startDateTime = new Date(`${b.appointmentDate}T${b.startTime}`);
          return b.status === "confirmed" && startDateTime > now;
        }).length,
      });
    } catch (err) {
    } finally {
      setLoadingBookings(false);
      setLoadingAnalytics(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div
          className="professionals-page"
          style={{ padding: "32px 0", maxWidth: 900, margin: "0 auto" }}
        >
          <p
            style={{
              fontSize: 15,
              color: "#6b7280",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            Loading...
          </p>
        </div>
      </SidebarLayout>
    );
  }

  if (!staff) {
    return (
      <SidebarLayout>
        <div
          className="professionals-page"
          style={{ padding: "32px 0", maxWidth: 900, margin: "0 auto" }}
        >
          <p
            style={{
              fontSize: 15,
              color: "#b91c1c",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            Staff member not found.
          </p>
        </div>
      </SidebarLayout>
    );
  }

  const fullName =
    `${capitalizeFirst(staff.firstName || "")} ${capitalizeFirst(staff.lastName || "")}`.trim();
  const roleFormatted = getProfessionalTypeLabel(staff);

  return (
    <SidebarLayout>
      <div
        className="professionals-page"
        style={{ padding: "32px 0", maxWidth: 900, margin: "0 auto" }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            marginBottom: 24,
            background: "none",
            border: "none",
            color: "#3b82f6",
            fontWeight: 600,
            fontSize: 15,
            cursor: "pointer",
            fontFamily: "var(--font-family-primary, Inter, sans-serif)",
          }}
        >
          &larr; Back
        </button>

        {/* Header Card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            padding: 32,
            marginBottom: 32,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 32,
                color: "#fff",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                flexShrink: 0,
              }}
            >
              {staff.firstName?.[0] || ""}
              {staff.lastName?.[0] || ""}
            </div>
            <div style={{ flex: 1 }}>
              <h1
                style={{
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  fontSize: 28,
                  fontWeight: 700,
                  margin: 0,
                  color: "#111827",
                }}
              >
                {fullName}
              </h1>
              <div
                style={{
                  margin: "8px 0",
                  color: "#6b7280",
                  fontSize: 16,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {roleFormatted}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginTop: 12,
                  flexWrap: "wrap",
                }}
              >
                {staff.email && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                      color: "#374151",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    <span style={{ fontSize: 16 }}>✉️</span>
                    <span>{staff.email}</span>
                  </div>
                )}
                {staff.phone && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                      color: "#374151",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    <span style={{ fontSize: 16 }}>📞</span>
                    <span>{staff.phone}</span>
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                padding: "6px 18px",
                borderRadius: 16,
                background: "#ecfdf5",
                color: "#059669",
                fontSize: 15,
                fontWeight: 600,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                height: "fit-content",
              }}
            >
              Active
            </div>
          </div>
        </div>

        {/* Information Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 24,
            marginBottom: 32,
          }}
        >
          {/* Location Info */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              padding: 24,
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: "0 0 16px 0",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                color: "#111827",
              }}
            >
              Location
            </h3>
            <div
              style={{
                fontSize: 15,
                color: "#374151",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                lineHeight: 1.6,
              }}
            >
              {staff.shopId?.city && staff.shopId?.state ? (
                <div style={{ fontWeight: 600 }}>
                  {capitalizeFirst(staff.shopId.city)},{" "}
                  {staff.shopId.state.toUpperCase()}
                </div>
              ) : (
                <span style={{ color: "#9ca3af" }}>No location assigned</span>
              )}
            </div>
          </div>

          {/* Timezone */}
          {staff.timezone && (
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                padding: 24,
              }}
            >
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  margin: "0 0 16px 0",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  color: "#111827",
                }}
              >
                Timezone
              </h3>
              <div
                style={{
                  fontSize: 15,
                  color: "#374151",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {staff.timezone}
              </div>
            </div>
          )}
        </div>

        {/* Bio */}
        {staff.bio && (
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              padding: 28,
              marginBottom: 32,
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: "0 0 16px 0",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                color: "#111827",
              }}
            >
              About
            </h3>
            <p
              style={{
                fontSize: 15,
                color: "#374151",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              {staff.bio}
            </p>
          </div>
        )}

        {/* Bookings and Analytics Sections */}
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}
        >
          {/* Bookings Summary */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              padding: 28,
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: "0 0 16px 0",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                color: "#111827",
              }}
            >
              Bookings
            </h3>
            {loadingBookings ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: 14,
                }}
              >
                Loading bookings...
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      padding: 16,
                      background: "#f9fafb",
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: "#3b82f6",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      {bookings.length}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                        marginTop: 4,
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      Total Bookings
                    </div>
                  </div>
                  <div
                    style={{
                      padding: 16,
                      background: "#ecfdf5",
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: "#10b981",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      {bookings.filter((b) => b.status === "completed").length}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#065f46",
                        marginTop: 4,
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      Completed
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    padding: 16,
                    background: "#dbeafe",
                    borderRadius: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#1e40af",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    {
                      bookings.filter(
                        (b) =>
                          b.status === "confirmed" &&
                          new Date(b.appointmentDate) > new Date(),
                      ).length
                    }
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#1e3a8a",
                      marginTop: 4,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    Upcoming Bookings
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Analytics Summary */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              padding: 28,
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: "0 0 16px 0",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                color: "#111827",
              }}
            >
              Analytics
            </h3>
            {loadingAnalytics ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: 14,
                }}
              >
                Loading analytics...
              </div>
            ) : analytics ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div
                  style={{
                    padding: 16,
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    borderRadius: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: "white",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    ${analytics.totalRevenue.toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.9)",
                      marginTop: 4,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    Total Revenue
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      padding: 16,
                      background: "#fef3c7",
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#b45309",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      ${analytics.monthlyRevenue.toFixed(2)}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#92400e",
                        marginTop: 4,
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      This Month
                    </div>
                  </div>
                  <div
                    style={{
                      padding: 16,
                      background: "#f3f4f6",
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#374151",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      {analytics.thisMonthBookings}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                        marginTop: 4,
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      Month Bookings
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: 14,
                }}
              >
                No analytics data available
              </div>
            )}
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default StaffProfile;
